import {Link} from "react-router-dom";
import {Breadcrumb} from "antd";
import className from "classnames/bind";
import FontAwesomeIcon from "../FontAwesome";
import {useCan} from "~/hooks";
import {useGetProjectQuery} from "~/reduxs/api/projectApiSlice";
import style from "./ProjectToolNav.module.scss";

const cn = className.bind(style);

/**
 * Điều hướng dùng chung cho các trang TOOL trong 1 Dự án (crawler/technical/gsc/ai):
 *  - Breadcrumb: Dự án > [Tên dự án] > [Tool] (> trail sâu hơn nếu có).
 *  - Thanh tab chuyển nhanh giữa các tool cùng dự án (gate theo useCan) — khỏi phải
 *    quay về dashboard dự án rồi bấm tool card khác.
 *
 * Props:
 *  - projectId : id dự án (bắt buộc).
 *  - active    : key tool đang mở ('overview' | 'crawler' | 'technical' | 'gsc' | 'ai').
 *  - trail     : [{label, to?}] — các mắt xích sâu hơn tool (vd "Crawl #12" > "Chi tiết trang").
 */
function ProjectToolNav({projectId, active = 'overview', trail = []}) {

	const canCrawl = useCan('crawl');
	const canGsc = useCan('gsc');
	const canAi = useCan('ai');

	const {data: project} = useGetProjectQuery(projectId, {skip: !projectId});

	const tools = [
		{key: 'overview', label: 'Tổng quan', icon: 'fa-light fa-folder-open', allowed: true},
		{key: 'crawler', label: 'Site Crawler', icon: 'fa-light fa-spider', allowed: canCrawl},
		{key: 'technical', label: 'Kỹ thuật SEO', icon: 'fa-light fa-screwdriver-wrench', allowed: canCrawl},
		{key: 'gsc', label: 'Search Console', icon: 'fa-light fa-magnifying-glass-chart', allowed: canGsc},
		{key: 'ai', label: 'AI SEO', icon: 'fa-light fa-wand-magic-sparkles', allowed: canAi},
	].filter((t) => t.allowed);

	const toolPath = (key) => (key === 'overview' ? `/projects/${projectId}` : `/projects/${projectId}/${key}`);

	const activeTool = tools.find((t) => t.key === active);

	const crumbs = [
		{title: <Link to="/projects">Dự án</Link>},
		{title: <Link to={`/projects/${projectId}`}>{project?.name || `#${projectId}`}</Link>},
	];

	if (activeTool && active !== 'overview') {
		crumbs.push({
			title: trail.length > 0
				? <Link to={toolPath(active)}>{activeTool.label}</Link>
				: activeTool.label,
		});
	}

	trail.forEach((item, i) => {
		crumbs.push({
			title: item.to && i < trail.length - 1 ? <Link to={item.to}>{item.label}</Link> : item.label,
		});
	});

	return (
		<div className={cn('wrap')}>
			<Breadcrumb className={cn('breadcrumb')} items={crumbs} />

			<div className={cn('tabs')}>
				{tools.map((t) => (
					<Link key={t.key} to={toolPath(t.key)}
						className={cn('tab', {active: t.key === active})}>
						<FontAwesomeIcon icon={t.icon} /> {t.label}
					</Link>
				))}
			</div>
		</div>
	);
}

export default ProjectToolNav;
